import {
  ArchiveOptions,
  ArchiveRecord,
  BulkArchiveItemsResponse,
  BulkUnarchiveItemsResponse,
} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {Permissions} from 'auth/PermissionConstants';
import {FolderSummary} from 'constants/FolderConstants';
import {SearchParams} from 'constants/NavConstants';
import {UnregisteredBioentity} from 'constants/RegistryConstants';
import {NavStoreSource} from 'constants/SourceConstants';
import {User} from 'constants/UserConstants';
import {BioentitySummary, FileSummary, Tag} from 'folder-items/FileConstants';
import {ShareLink} from 'nav/share/ShareLinkConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import FolderDAOCache from './FolderDAOCache';

export interface FileBrowserSearchResults {
  data: Array<{id: string}>;
  limit: number;
  total: number;
}

export function fetchSources(): JQueryPromise<{data: Array<NavStoreSource>}> {
  return net.getJSON('/1/api/sources/');
}

export function fetchSource(apiId: string): JQueryPromise<NavStoreSource> {
  return net.getJSON(`/1/api/sources/${apiId}`);
}

export function search(queryParams: SearchParams): JQueryPromise<FileBrowserSearchResults> {
  return net.getJSON('/1/api/all/search', queryParams);
}

export function fetchRecents(): JQueryPromise<{data: Array<FileSummary>}> {
  return net.getJSON('/1/api/users/me/recent/');
}

export function bulkFetchFileSummaries(
  fileIds: Array<string>,
  shouldErrorIfInaccessible?: boolean
): JQueryPromise<{fileSummariesById: {[id: string]: BioentitySummary}}> {
  return net.postJSON({
    url: '/1/api/files:bulk-get-summaries',
    data: {fileIds, shouldErrorIfInaccessible},
  });
}

export function updateApiIdentified(
  apiId: string,
  update: {name: string; folder?: string},
  {parentFolderId}: {parentFolderId?: string},
  dataModsHeader?: DataModificationsHeader
): Promise<BioentitySummary> {
  return net
    .patchJSONPromise({
      url: `/1/api/files/${apiId}`,
      data: update,
      extraHeaders: dataModsHeader,
    })
    .then((res) => {
      if (update.folder != null) {
        FolderDAOCache.invalidateFolderContents(update.folder);
      }
      if (APIUtil.isFolder(apiId)) {
        FolderDAOCache.invalidateFolder(apiId);
      }
      if (parentFolderId != null) {
        FolderDAOCache.invalidateFolderContents(parentFolderId);
      }
      return res;
    });
}

export function deleteFiles(
  fileIds: Array<string>,
  {parentFolderIds}: {parentFolderIds: Array<string>}
): JQueryPromise<{}> {
  return TaskRunner.postJSON({
    url: '/1/api/files/actions/delete',
    data: {ids: fileIds},
  }).done(() => {
    const deletedFolderIds = fileIds.filter((id) => APIUtil.isFolder(id));
    for (const folderId of Array.from(parentFolderIds)) {
      FolderDAOCache.invalidateFolderContents(folderId);
    }
    for (const folderId of Array.from(deletedFolderIds)) {
      FolderDAOCache.invalidateFolder(folderId);
    }
  });
}

/**
 * This endpoint bypasses check auth for Intent.DELETE and should ONLY be used when delete is
 * sanctioned, e.g. from Import Sequences modal immediately after creation.
 */
export function forceDeleteFiles(
  fileIds: Array<string>,
  {parentFolderIds}: {parentFolderIds: Array<string>}
): JQueryPromise<{}> {
  return net
    .postJSON({
      url: '/1/api/files:force-delete',
      data: {ids: fileIds},
    })
    .done(() => {
      for (const folderId of Array.from(parentFolderIds)) {
        FolderDAOCache.invalidateFolderContents(folderId);
      }
    });
}

export function deleteShortcuts(
  fileIds: Array<string>,
  {parentFolderIds}: {parentFolderIds: Array<string>}
): JQueryPromise<{}> {
  return net
    .postJSON({
      url: '/1/api/files/actions/delete-shortcuts',
      data: {ids: fileIds},
    })
    .done(() => {
      for (const dirtyFolderId of Array.from(parentFolderIds)) {
        FolderDAOCache.invalidateFolderContents(dirtyFolderId);
      }
    });
}

export function getFilesForDelete(
  fileIds: Array<string>,
  shouldDetectShortcuts: boolean
): JQueryPromise<{filesForDelete: Array<FileSummary>; foldersForDelete: Array<FolderSummary>}> {
  if (shouldDetectShortcuts == null) {
    shouldDetectShortcuts = false;
  }
  return net.postJSON({
    url: '/1/api/files/actions/get-files-for-delete',
    data: {ids: fileIds, shouldDetectShortcuts},
  });
}

export interface BulkArchiveFilesResponse extends BulkArchiveItemsResponse {
  permissionsByItemId: {[itemId: string]: Permissions};
  archiveRecordsByItemId: {[itemId: string]: ArchiveRecord};
  unregisterDataByItemId: {
    [itemId: string]: UnregisteredBioentity;
  };
}

export function bulkArchive(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveShortcuts,
    shouldUnlinkParts,
    shouldUnregisterBioentities,
    unregisteredBioentitiesFolderId,
    shouldRemoveBarcodes,
    shouldArchiveContainedSamples,
    resultPurpose,
  }: ArchiveOptions,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<BulkArchiveFilesResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/files:archive',
    data: {
      ids,
      purpose,
      shouldRemoveShortcuts,
      shouldUnlinkParts,
      shouldUnregisterBioentities,
      unregisteredBioentitiesFolderId,
      shouldRemoveBarcodes,
      shouldArchiveContainedSamples,
      resultPurpose,
    },
    extraHeaders: dataModsHeader,
  });
}

export interface BulkUnarchiveFilesResponse extends BulkUnarchiveItemsResponse {
  permissionsByItemId: {[itemId: string]: Permissions};
}

export function bulkUnarchive(
  ids: Array<string>,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<BulkUnarchiveFilesResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/files:unarchive',
    data: {ids},
    extraHeaders: dataModsHeader,
  });
}

function _moveHelper(
  url: string,
  fileIds: Array<string>,
  folderId: string,
  {parentFolderIds}: {parentFolderIds: Array<string>},
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{}> {
  return net
    .postJSON({
      url,
      data: {
        ids: fileIds,
        folderId,
      },
      extraHeaders: dataModsHeader,
    })
    .done(() => {
      const movedFolderIds = fileIds.filter((id) => APIUtil.isFolder(id));
      for (folderId of Array.from(parentFolderIds)) {
        FolderDAOCache.invalidateFolderContents(folderId);
      }
      for (folderId of Array.from(movedFolderIds)) {
        FolderDAOCache.invalidateFolder(folderId);
      }
    });
}

export function moveFiles(
  fileIds: Array<string>,
  folderId: string,
  {parentFolderIds}: {parentFolderIds: Array<string>},
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{}> {
  return _moveHelper('/1/api/files/actions/move', fileIds, folderId, {parentFolderIds}, dataModsHeader);
}

export function moveShortcuts(
  fileIds: Array<string>,
  folderId: string,
  {parentFolderIds}: {parentFolderIds: Array<string>},
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{}> {
  return _moveHelper(
    '/1/api/files/actions/move-shortcuts',
    fileIds,
    folderId,
    {parentFolderIds},
    dataModsHeader
  );
}

export function copyFiles(
  fileIds: Array<string>,
  folderId: string
): JQueryPromise<{fileCopyIds: Array<string>}> {
  LoadingScreen.show('Copying...');
  return (
    TaskRunner.postJSON({
      url: '/1/api/files:copy',
      data: {
        ids: fileIds,
        folderId,
      },
    })
      .always(() => {
        LoadingScreen.hide();
      })
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      .done(() => FolderDAOCache.invalidateFolderContents(folderId))
  );
}

export function bulkGetTags(
  fileIds: Array<string>
): JQueryPromise<{data: Array<{id: string; tags: Array<Tag>}>}> {
  return net.postJSON({
    url: '/1/api/files/actions/bulk-get-tags',
    data: {
      ids: fileIds,
    },
  });
}

export function updateTags(
  fileIds: Array<string>,
  {
    toSet,
    toAdd,
    toRemove,
    toRename,
  }: {
    toSet: {[id: string]: {[value: string]: string}};
    toAdd: Array<string>;
    toRemove: Array<string>;
    toRename: {[id: string]: string};
  },
  dataModsHeader?: DataModificationsHeader
): Promise<{data: Array<{id: string; tags: Array<Tag>}>}> {
  return net.postJSONPromise({
    url: '/1/api/files/actions/tag',
    data: {
      ids: fileIds,
      toSet,
      toAdd,
      toRemove,
      toRename,
    },
    extraHeaders: dataModsHeader,
  });
}

export function exportTagsToCSV(fileIds: Array<string>): JQueryPromise<{url: string}> {
  return TaskRunner.runTask({
    contentType: 'application/json',
    data: JSON.stringify({ids: fileIds}),
    dataType: 'json',
    type: 'POST',
    url: '/1/api/files/actions/export-tags-to-csv',
  });
}

export function fetchShareLink(id: string): JQueryPromise<ShareLink> {
  return net.getJSON(`/1/api/files/${id}/share-link`);
}

export function createShareLink(id: string): JQueryPromise<ShareLink> {
  return net.putJSON({url: `/1/api/files/${id}/share-link`, data: {}});
}

export function updateShareLink(shareIdentifier: string, data: {isActive: boolean}): Promise<ShareLink> {
  return net.patchJSONPromise({url: `/1/api/share-links/${shareIdentifier}`, data});
}

export function bulkGetAuthors(fileIds: Array<string>): Promise<{[id: string]: Array<User>}> {
  return net.postJSONPromise({
    url: `/1/api/files:bulk-get-authors`,
    data: {fileIds},
  });
}

export function updateAuthors(
  id: string,
  authorIds: Array<string>,
  dataModsHeader?: DataModificationsHeader
): Promise<{authors: Array<User>; filePermissions: Permissions}> {
  return net.putJSONPromise({
    url: `/1/api/files/${id}/authors`,
    data: {authorIds},
    extraHeaders: dataModsHeader,
  });
}

export function validateAuthors(
  id: string,
  authorIds: Array<string>
): Promise<{warnings: Array<{message: string}>}> {
  return net.postJSONPromise({
    url: `/1/api/files/${id}:validate-authors`,
    data: {authorIds},
  });
}
